import React, { useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Card, CardContent, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

// Riwayat refund untuk pengawasan: siapa me-refund apa, kapan, dan alasannya. Server: GET /refund.
const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const waktu = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const tgl = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };

const RiwayatRefund = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return tgl(d); });
  const [sampai, setSampai] = useState(() => tgl(new Date()));
  const [cabang, setCabang] = useState('');
  const query = new URLSearchParams({ dari, sampai, ...(cabang && { cabang }) }).toString();
  const { data, error, isLoading } = useSWR(`${base()}/refund?${query}`, ambil, { revalidateOnFocus: false });
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];
  const list = Array.isArray(data?.data) ? data.data : [];
  const r = data?.ringkasan || {};

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Riwayat Refund</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Semua refund per item {superadmin ? 'di semua cabang' : 'di cabang Anda'}. Refund hanya bisa dilakukan admin cabang dan superadmin dari halaman Transaksi.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" InputLabelProps={{ shrink: true }} value={dari} onChange={(e) => setDari(e.target.value)} />
        <TextField size="small" type="date" label="Sampai" InputLabelProps={{ shrink: true }} value={sampai} onChange={(e) => setSampai(e.target.value)} />
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Semua cabang</MenuItem>
            {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2, maxWidth: 480 }}>
        {[['Jumlah refund', r.jumlah ?? 0, 'text.primary'], ['Total dikembalikan', rupiah(r.totalRefund || 0), 'warning.dark']].map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat riwayat refund'}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Waktu</TableCell>
              {superadmin && <TableCell>Cabang</TableCell>}
              <TableCell>Order</TableCell>
              <TableCell>Barang</TableCell>
              <TableCell>Alasan</TableCell>
              <TableCell>Petugas</TableCell>
              <TableCell align="right">Dikembalikan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7}>Memuat…</TableCell></TableRow>}
            {!isLoading && list.length === 0 && <TableRow><TableCell colSpan={7}>Belum ada refund pada rentang ini.</TableCell></TableRow>}
            {list.map((x) => (
              <TableRow key={x.uuid} hover>
                <TableCell>{waktu(x.createdAt)}</TableCell>
                {superadmin && <TableCell>{x.namacabang || '-'}</TableCell>}
                <TableCell>{x.orderId}<Typography variant="caption" color="text.secondary" display="block">{x.pembayaran}</Typography></TableCell>
                <TableCell>
                  {(x.items || []).map((i, k) => (
                    <Typography key={k} variant="body2">{i.jumlah}× {i.namabarang}{i.kembalikanStok ? ' (kembali ke stok)' : ''}</Typography>
                  ))}
                </TableCell>
                <TableCell>{x.alasanLabel}{x.catatan && <Typography variant="caption" color="text.secondary" display="block">{x.catatan}</Typography>}</TableCell>
                <TableCell>{x.petugas || '-'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{rupiah(x.totalRefund)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RiwayatRefund;
